import { IOptionMenuItem } from '@/document-edit/assembler/types/option-menu-item';
import { ICell, IRow, IBlockWidth, IImage } from '@/editor/types';
import {
  EBlockWidth,
  EBreakpoint,
  EImageSize,
  EMoveDirection,
  ENodeType,
} from '@/editor/types/enums';

export class EditorHelper {
  static getMoveOptions(
    isFirstRowSelected: boolean,
    isLastRowSelected: boolean
  ): IOptionMenuItem[] {
    const moveOptions: IOptionMenuItem[] = [
      {
        icon: 'editor-move-to-top',
        title: 'move-to-top',
        callback: EMoveDirection.ToTop,
        preventFillIcon: true,
      },
      {
        icon: 'editor-move-up',
        title: 'move-up',
        callback: EMoveDirection.Up,
        preventFillIcon: true,
      },
      {
        icon: 'editor-move-down',
        title: 'move-down',
        callback: EMoveDirection.Down,
        preventFillIcon: true,
      },
      {
        icon: 'editor-move-to-bottom',
        title: 'move-to-bottom',
        callback: EMoveDirection.ToBottom,
        preventFillIcon: true,
      },
    ];

    if (isLastRowSelected && isFirstRowSelected) {
      return [];
    } else if (isFirstRowSelected) {
      return moveOptions.filter(({ callback }) =>
        [EMoveDirection.Down, EMoveDirection.ToBottom].includes(callback)
      );
    } else if (isLastRowSelected) {
      return moveOptions.filter(({ callback }) =>
        [EMoveDirection.Up, EMoveDirection.ToTop].includes(callback)
      );
    }

    return moveOptions;
  }

  static hasHiddenContentRight({ offsetWidth, scrollWidth, scrollLeft }: HTMLElement): boolean {
    if (scrollWidth <= offsetWidth) {
      return false;
    }

    return offsetWidth + scrollLeft !== scrollWidth;
  }

  static isBlockWidth(size: EBlockWidth, width: number): boolean {
    switch (size) {
      case EBlockWidth.XXSmall:
        return width <= EBreakpoint.XXSmall;
      case EBlockWidth.XSmall:
        return width <= EBreakpoint.XSmall;
      case EBlockWidth.Small:
        return width <= EBreakpoint.Small;
      case EBlockWidth.Medium:
        return width <= EBreakpoint.Medium;
      case EBlockWidth.Large:
        return width <= EBreakpoint.Large;
      case EBlockWidth.XLarge:
        return width > EBreakpoint.Large;
    }
  }

  static getBlockWidth(width: number): IBlockWidth {
    return {
      xxs: EditorHelper.isBlockWidth(EBlockWidth.XXSmall, width),
      xs: EditorHelper.isBlockWidth(EBlockWidth.XSmall, width),
      sm: EditorHelper.isBlockWidth(EBlockWidth.Small, width),
      md: EditorHelper.isBlockWidth(EBlockWidth.Medium, width),
      lg: EditorHelper.isBlockWidth(EBlockWidth.Large, width),
      xl: EditorHelper.isBlockWidth(EBlockWidth.XLarge, width),
    };
  }

  static getBlockWidthClass(blockWidth: IBlockWidth): EBlockWidth {
    if (blockWidth?.xxs) {
      return EBlockWidth.XXSmall;
    } else if (blockWidth?.xs) {
      return EBlockWidth.XSmall;
    } else if (blockWidth?.sm) {
      return EBlockWidth.Small;
    } else if (blockWidth?.md) {
      return EBlockWidth.Medium;
    } else if (blockWidth?.lg) {
      return EBlockWidth.Large;
    } else if (blockWidth?.xl) {
      return EBlockWidth.XLarge;
    } else {
      return EBlockWidth.XXSmall;
    }
  }

  static rowContainsCoverImage({ cells }: IRow): boolean {
    const imageCoverNode = (node: IImage) =>
      node.type === ENodeType.Image && node.imageSize === EImageSize.Cover;

    return !!cells.find(({ nodes }: ICell) => !!nodes.find(imageCoverNode));
  }

  static deepCopy(src, _visited?, _copiesVisited?) {
    if (src === null || typeof src !== 'object') {
      return src;
    }
    //Honor native/custom clone methods
    if (typeof src.clone == 'function') {
      return src.clone(true);
    }
    if (src instanceof Date) {
      return new Date(src.getTime());
    }
    if (src instanceof RegExp) {
      return new RegExp(src);
    }
    //DOM Element
    if (src.nodeType && typeof src.cloneNode == 'function') {
      return src.cloneNode(true);
    }
    // Initialize the visited objects arrays if needed.
    // This is used to detect cyclic references.
    if (_visited === undefined) {
      _visited = [];
      _copiesVisited = [];
    }
    // Check if this object has already been visited
    for (let index = 0; index < _visited.length; index++) {
      // If so, get the copy we already made
      if (src === _visited[index]) {
        return _copiesVisited[index];
      }
    }
    //Array
    if (Object.prototype.toString.call(src) == '[object Array]') {
      //[].slice() by itself would soft clone
      const ret = src.slice();
      //add it to the visited array
      _visited.push(src);
      _copiesVisited.push(ret);
      let index = ret.length;
      while (index--) {
        ret[index] = this.deepCopy(ret[index], _visited, _copiesVisited);
      }
      return ret;
    }
    //If we've reached here, we have a regular object
    //make sure the returned object has the same prototype as the original
    const proto = Object.getPrototypeOf(src);
    const dest = Object.create(proto);
    //add this object to the visited array
    _visited.push(src);
    _copiesVisited.push(dest);
    for (const key in src) {
      dest[key] = this.deepCopy(src[key], _visited, _copiesVisited);
    }
    return dest;
  }
}
